import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, describeApiError } from "@/lib/api";
import { Warning, ArrowRight, CheckCircle, ShieldCheck, CircleNotch, Copy } from "@phosphor-icons/react";
import { DuplicateCompareDialog } from "@/components/DuplicateCompareDialog";

const money = (n) => `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function AnomalyAlertsCard() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState("");
  const [compareAnomaly, setCompareAnomaly] = useState(null);

  const load = () => {
    api.get("/anomalies/summary").then((r) => setSummary(r.data)).catch(() => {
      setSummary({ open_count: 0, high_count: 0, review_count: 0, rowan_primary: "Couldn't load anomaly monitoring yet.", items: [] });
    });
  };

  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    setBusy(`${action}-${id}`);
    try {
      await api.post(`/anomalies/${id}/${action}`);
      toast.success(action === "dismiss" ? "Rowan learned from that one." : "Flagged for review.");
      load();
    } catch (err) {
      toast.error(describeApiError(err));
    } finally {
      setBusy("");
    }
  };

  if (!summary) return null;

  return (
    <section className="rounded-[28px] p-6 sm:p-7 mb-10 overflow-hidden relative" style={{ background: "linear-gradient(135deg, rgba(253,236,232,0.98) 0%, rgba(255,249,240,0.98) 100%)", border: "1px solid rgba(217,108,74,0.28)" }} data-testid="dashboard-anomaly-card">
      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ background: "rgba(255,255,255,0.72)", color: "#B23B1F" }}>
            <Warning size={14} weight="duotone" /> Anomaly watch
          </div>
          <h3 className="font-display text-2xl sm:text-3xl font-bold mt-3" style={{ color: "var(--bk-text)" }}>Rowan flagged a few transactions</h3>
          <p className="text-sm mt-2 leading-relaxed" style={{ color: "var(--bk-text-2)" }} data-testid="dashboard-anomaly-rowan">
            {summary.rowan_primary}
          </p>
        </div>
        {summary.items?.[0]?.business_id && (
          <button onClick={() => navigate(`/business/${summary.items[0].business_id}?tab=books`)} data-testid="dashboard-anomaly-open-books" className="bk-btn-primary px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
            Open books <ArrowRight size={15} weight="bold" />
          </button>
        )}
      </div>

      <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        <div className="bk-card p-5" data-testid="dashboard-anomaly-open-count">
          <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Unreviewed</p>
          <p className="font-display text-3xl font-bold mt-2" style={{ color: summary.open_count > 0 ? "#B23B1F" : "var(--bk-success)" }}>{summary.open_count || 0}</p>
          <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>Across {summary.business_count || 0} business{summary.business_count === 1 ? "" : "es"}</p>
        </div>
        <div className="bk-card p-5" data-testid="dashboard-anomaly-high-count">
          <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>High priority</p>
          <p className="font-display text-3xl font-bold mt-2" style={{ color: summary.high_count > 0 ? "#B23B1F" : "var(--bk-text)" }}>{summary.high_count || 0}</p>
          <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>Likely duplicates, personal, or unusually large</p>
        </div>
        <div className="bk-card p-5" data-testid="dashboard-anomaly-review-count">
          <p className="text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Flagged for review</p>
          <p className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{summary.review_count || 0}</p>
          <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }}>Escalated into the audit trail</p>
        </div>
      </div>

      <div className="relative mt-6 space-y-3" data-testid="dashboard-anomaly-list">
        {summary.items?.length ? summary.items.map((item) => {
          const dismissBusy = busy === `dismiss-${item.id}`;
          const reviewBusy = busy === `review-${item.id}`;
          return (
            <div key={item.id} className="rounded-[22px] border bg-white/90 p-4 flex flex-wrap items-start justify-between gap-3" style={{ borderColor: "rgba(217,108,74,0.2)" }} data-testid={`dashboard-anomaly-item-${item.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>{item.title}</p>
                  <span className="text-[11px] font-bold px-2 py-1 rounded-full" style={{ background: item.severity === "high" ? "#FDECE8" : "#FFF4D9", color: item.severity === "high" ? "#B23B1F" : "#8A6A3B" }} data-testid={`dashboard-anomaly-severity-${item.id}`}>{item.severity}</span>
                  {item.business_name && <span className="text-[11px] font-semibold px-2 py-1 rounded-full" style={{ background: "var(--bk-page)", color: "var(--bk-accent)" }}>{item.business_name}</span>}
                </div>
                <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>{item.transaction_description} · {money(item.transaction_amount)} · {item.transaction_date}</p>
                <p className="text-sm mt-2" style={{ color: "var(--bk-text)" }}>{item.detail}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">
                {item.anomaly_type === "duplicate_charge" && (
                  <button onClick={() => setCompareAnomaly(item)} disabled={dismissBusy || reviewBusy} data-testid={`dashboard-anomaly-compare-${item.id}`} className="px-3 py-2 rounded-full text-xs font-bold text-white disabled:opacity-60 inline-flex items-center gap-1" style={{ background: "#B23B1F" }}>
                    <Copy size={12} weight="bold" /> See both charges
                  </button>
                )}
                <button onClick={() => navigate(`/business/${item.business_id}?tab=books`)} data-testid={`dashboard-anomaly-open-${item.id}`} className="px-3 py-2 rounded-full text-xs font-semibold border" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                  Open
                </button>
                <button onClick={() => act(item.id, "dismiss")} disabled={dismissBusy || reviewBusy} data-testid={`dashboard-anomaly-dismiss-${item.id}`} className="px-3 py-2 rounded-full text-xs font-semibold border disabled:opacity-60" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                  {dismissBusy ? <span className="inline-flex items-center gap-1"><CircleNotch size={12} className="animate-spin" /> Saving…</span> : <span className="inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Looks fine</span>}
                </button>
                <button onClick={() => act(item.id, "review")} disabled={dismissBusy || reviewBusy} data-testid={`dashboard-anomaly-review-${item.id}`} className="bk-btn-primary px-3 py-2 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-60">
                  {reviewBusy ? <><CircleNotch size={12} className="animate-spin" /> Saving…</> : <><ShieldCheck size={12} weight="fill" /> Flag for review</>}
                </button>
              </div>
            </div>
          );
        }) : (
          <div className="rounded-[22px] border bg-white/90 p-5" style={{ borderColor: "rgba(91,138,111,0.24)" }} data-testid="dashboard-anomaly-empty">
            <p className="text-sm font-semibold" style={{ color: "var(--bk-text)" }}>All clear.</p>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>No unusual spend patterns are open right now. Rowan is quietly keeping watch in the background.</p>
          </div>
        )}
      </div>

      <DuplicateCompareDialog
        anomaly={compareAnomaly}
        onClose={() => setCompareAnomaly(null)}
        onResolved={load}
      />
    </section>
  );
}